import React, { useEffect, useMemo, useState } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

import { useStudents } from '../../context/StudentsContext';
import { useIEPGoals } from '../../context/IEPGoalsContext';
import { useIncidents } from '../../context/IncidentsContext';
import { analyzeCaseload } from '../../lib/insights/analytics';
import { isNarrativeAvailable } from '../../lib/insights/narrative';

import CaseloadSummaryTiles from './components/CaseloadSummaryTiles';
import SignalBreakdownCard from './components/SignalBreakdownCard';
import PatternsList from './components/PatternsList';
import RecommendationsList from './components/RecommendationsList';
import StudentInsightList from './components/StudentInsightList';
import StudentInsightPanel from './components/StudentInsightPanel';

// ---------------------------------------------------------------------------
// AI Insights & Recommendations
//
// Complete rewrite. Previously this page:
//   * showed a bright "Not yet live — sample data" banner over everything
//   * hardcoded four fake insights, three fake patterns, three fake
//     recommendations, four fake actions, and a "trend chart" of numbers
//     that had no relationship to any real caseload
//   * had a "Refresh Analysis" button that just spun for 2 seconds and
//     set an in-memory timestamp
//
// It's now:
//   * A real analytics run over the four contexts the app already has —
//     students, IEP goals, progress entries, behavior incidents.
//   * A caseload summary + signal breakdown + real patterns + real
//     recommendations (all deterministic — no LLM required to see them).
//   * A student list ranked by signal, with a deep-dive panel that
//     renders per-goal trajectory, per-behavior rollups, and a likely
//     function of behavior.
//   * Optional AI-written narratives on the student panel: a plain-
//     English summary, a bullet list of PBIS interventions, and a
//     drafted email to a caregiver. All three go through the same
//     shared Supabase Edge Function (`draft-goal`, which now dispatches
//     on a `kind` field). Zero teacher setup, zero exposed API keys.
//
// If the Edge Function isn't deployed (or the admin hasn't set an AI
// provider secret), every deterministic number on this page still
// renders — only the "Get AI narrative" buttons degrade to "not set up
// yet" panels.
// ---------------------------------------------------------------------------

const AIInsightsPage = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const { students, loading: studentsLoading } = useStudents();
  const { goals, listAllProgressEntries, loading: goalsLoading } = useIEPGoals();
  const { incidents, loading: incidentsLoading } = useIncidents();

  const [progressEntries, setProgressEntries] = useState([]);
  const [progressLoading, setProgressLoading] = useState(true);
  const [progressError, setProgressError] = useState(null);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [computedAt, setComputedAt] = useState(new Date());

  const narrativeAvailable = isNarrativeAvailable();

  // Load ALL progress entries once — the analytics library needs every
  // point (not a windowed slice) so it can compute long-horizon velocity
  // and staleness. We reload on `computedAt` to give the "Recompute"
  // button something to do.
  useEffect(() => {
    let cancelled = false;
    setProgressLoading(true);
    setProgressError(null);
    listAllProgressEntries()
      .then((rows) => {
        if (cancelled) return;
        setProgressEntries(Array.isArray(rows) ? rows : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setProgressError(err?.message || 'Failed to load progress entries.');
      })
      .finally(() => {
        if (!cancelled) setProgressLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listAllProgressEntries, computedAt]);

  const analytics = useMemo(
    () =>
      analyzeCaseload({
        students,
        goals,
        incidents,
        progressEntries,
        now: computedAt,
      }),
    [students, goals, incidents, progressEntries, computedAt],
  );

  // Auto-select the first student in the list (highest-signal by sort
  // order) as soon as the caseload finishes loading. The teacher can
  // click any other student to swap the panel.
  useEffect(() => {
    if (selectedStudentId) return;
    if (!analytics.studentInsights.length) return;
    const sorted = [...analytics.studentInsights].sort((a, b) => {
      const rank = { critical: 5, attention: 4, strong: 3, steady: 2, quiet: 1 };
      return (rank[b.signal] || 0) - (rank[a.signal] || 0);
    });
    setSelectedStudentId(sorted[0].studentId);
  }, [analytics, selectedStudentId]);

  const selectedInsight = analytics.studentInsights.find(
    (s) => s.studentId === selectedStudentId,
  );

  const resolveStudentNames = (ids) => {
    if (!Array.isArray(ids)) return [];
    return ids
      .map((id) => analytics.studentInsights.find((s) => s.studentId === id))
      .filter(Boolean)
      .map((s) => s.studentName);
  };

  const isLoading = studentsLoading || goalsLoading || incidentsLoading || progressLoading;

  const handleMenuToggle = () => setIsSidebarOpen((v) => !v);
  const handleSidebarCollapse = () => setIsSidebarCollapsed((v) => !v);
  const handleRecompute = () => setComputedAt(new Date());

  return (
    <div className="min-h-screen bg-slate-50">
      <Header onMenuToggle={handleMenuToggle} isMenuOpen={isSidebarOpen} />
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={handleSidebarCollapse}
      />
      <main
        className={`transition-all duration-300 ${
          isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'
        } pt-16`}
      >
        <div className="p-6">
          <Breadcrumb />

          {/* Page header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 gap-4">
            <div>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <Icon name="Brain" size={20} className="text-white" />
                </div>
                <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">
                  AI Insights
                </h1>
              </div>
              <p className="text-slate-600 mt-2 max-w-2xl">
                A quick summary of what your caseload data is showing:
                who's making progress, whose behavior is changing,
                which IEP reviews are coming up. Every number here
                comes from the incidents + goal data you've logged —
                no fake insights. Optional AI can turn the numbers
                into paragraphs you can paste into a progress report
                or a parent email.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Icon name="Clock" size={14} />
                <span>Recomputed {computedAt.toLocaleTimeString()}</span>
              </div>
              <Button
                variant="outline"
                onClick={handleRecompute}
                loading={isLoading}
                iconName="RefreshCw"
                iconPosition="left"
              >
                Recompute
              </Button>
            </div>
          </div>

          {progressError && (
            <div className="mb-4 rounded bg-red-50 border border-red-200 p-3 text-sm text-red-700">
              Couldn't load progress entries: {progressError}
            </div>
          )}

          {!narrativeAvailable && (
            <div className="mb-6 rounded bg-slate-100 border border-slate-200 p-3 text-xs text-slate-600 flex items-start gap-2">
              <Icon name="Info" size={14} className="text-slate-500 flex-shrink-0 mt-0.5" />
              <span>
                Supabase isn't configured in this build, so the "Ask AI" buttons
                are hidden — everything below still runs against your local
                demo data.
              </span>
            </div>
          )}

          {students.length === 0 && !isLoading ? (
            <div className="bg-white border border-slate-200 rounded-lg p-10 text-center">
              <Icon name="Users" size={22} className="text-slate-400 mx-auto mb-2" />
              <h3 className="font-semibold text-slate-800">No students yet</h3>
              <p className="text-sm text-slate-500 mt-1">
                Add students, IEP goals, and behavior incidents; then this page
                will start showing your caseload trends.
              </p>
            </div>
          ) : (
            <>
              {/* Summary tiles */}
              <div className="mb-6">
                <CaseloadSummaryTiles summary={analytics.summary} />
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
                {/* Left column — signal, patterns, recommendations */}
                <div className="xl:col-span-4 space-y-4">
                  <SignalBreakdownCard summary={analytics.summary} />

                  <div>
                    <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
                      Patterns
                    </h2>
                    <PatternsList
                      patterns={analytics.patterns}
                      resolveStudentNames={resolveStudentNames}
                      onOpenStudent={(id) => setSelectedStudentId(id)}
                    />
                  </div>

                  <div>
                    <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
                      Recommended next steps
                    </h2>
                    <RecommendationsList recommendations={analytics.recommendations} />
                  </div>
                </div>

                {/* Middle column — the student panel */}
                <div className="xl:col-span-5">
                  <StudentInsightPanel
                    insight={selectedInsight}
                    aiEnabled={narrativeAvailable}
                  />
                </div>

                {/* Right column — student list */}
                <div className="xl:col-span-3">
                  <StudentInsightList
                    studentInsights={analytics.studentInsights}
                    selectedId={selectedStudentId}
                    onSelect={setSelectedStudentId}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default AIInsightsPage;
